import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import Chip from '@mui/material/Chip';

/**
 * Props for the UserStatusChip component.
 */
interface UserStatusChipProps {
  /** Whether the user is active */
  isActive?: boolean;
  /** Whether the user needs onboarding (computed from backend) */
  needsOnboarding?: boolean;
  /** When the onboarding email was sent */
  onboardingSentAt?: string | null;
  /** When the account was activated */
  activatedAt?: string | null;
}

/**
 * Displays a simple status chip for users showing their current state.
 *
 * Four possible states:
 * 1. Inactive: User account is inactive (active is false)
 * 2. Not Onboarded: User imported without password, onboarding not sent yet
 * 3. Onboarding Pending: Onboarding email sent but not activated yet
 * 4. Active: User is active and has completed onboarding if required
 */
export function UserStatusChip({
  isActive: active,
  needsOnboarding,
  onboardingSentAt,
  activatedAt,
}: UserStatusChipProps) {
  // State 1: User is inactive
  if (active === false) {
    return <Chip icon={<CancelIcon />} label="Account Inactive" color="error" size="medium" variant="outlined" />;
  }

  // State 2: User needs onboarding but hasn't been sent an email yet
  if (needsOnboarding && !onboardingSentAt) {
    return <Chip icon={<InfoIcon />} label="Not Onboarded" color="info" size="medium" variant="outlined" />;
  }

  // State 3: Onboarding email sent but user hasn't activated yet
  if (needsOnboarding && onboardingSentAt && !activatedAt) {
    return <Chip icon={<WarningIcon />} label="Onboarding Pending" color="warning" size="medium" variant="outlined" />;
  }

  // State 4: User is active (either never needed onboarding or completed it)
  if (active === true || activatedAt) {
    return <Chip icon={<CheckCircleIcon />} label="Active" color="success" size="medium" variant="outlined" />;
  }

  // Default: don't show anything if status is unclear
  return null;
}
